import { ErrorHandler, Injectable, OnDestroy } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { CaptureContext } from '@sentry/types';
import { environment } from '@root/src/environments/environment';
import {
    Observable, Subject, catchError, debounceTime,
    filter, map, of, switchMap, tap, withLatestFrom
} from 'rxjs';
import { Exception } from '@root/src/shared/domain/dtos/exception.dto';

// caution with non-public imports
import { ErrorExtractorService } from '@root/src/app/services/error-extractor.service';
import { LoggingApiService } from '@root/src/app/services/logging-api.service';
import { FeatureFlagService } from '@root/src/app/services/feature-flag.service';

@Injectable({
    providedIn: 'root'
})
export class PublicGlobalErrorHandler implements ErrorHandler, OnDestroy {
    private readonly sentryErrorSubject: Subject<Error> = new Subject<Error>();

    constructor(
        private flagService: FeatureFlagService,
        private loggingApiService: LoggingApiService,
        private errorExtractorService: ErrorExtractorService
    ) {
        this.initializeSentrySubscription();
    }

    handleError(error: Error): void {
        if (!error) {
            return;
        }

        console.error(error);
        this.logError(error);

        if (Array.isArray(error)) {
            error.forEach(x => this.sentryErrorSubject.next(x));
        } else {
            this.sentryErrorSubject.next(error);
        }
    }

    ngOnDestroy(): void {
        this.sentryErrorSubject.complete();
    }

    private initializeSentrySubscription(): void {
        this.sentryErrorSubject.pipe(
            filter(() => environment.sentry.enabled === 'true'),
            debounceTime(100),
            map(error => this.errorExtractorService.extractError(error)),
            withLatestFrom(this.flagService.retrievePublicFlag(this.flagService.keys.UseSentryForManagerClient)),
            filter(([, isFlagEnabled]) => isFlagEnabled),
            switchMap(([error]) => this.getErrorContext(error)),
            tap(errorContext => Sentry.captureException(errorContext.error, errorContext.context))
        ).subscribe();
    }

    private getErrorContext(error: Error | string): Observable<{ error: Error | string, context: CaptureContext }> {
        // userInfo intentionally 'undefined', since we're unauthenticated
        return of(undefined).pipe(
            catchError(() => of(undefined)),
            map(userInfo => this.updateContext(error, userInfo)),
            map(context => {
                return {
                    error,
                    context
                };
            })
        );
    }

    private updateContext(error: Error | string, userInfo?: { Id: number, Email: string }): CaptureContext {
        if (userInfo) {
            Sentry.setUser({
                email: userInfo.Email,
                id: userInfo.Id.toString(),
                username: userInfo.Email
            });
        }

        if (error instanceof Error) {
            Sentry.getCurrentScope().setTransactionName(error.message);
        } else {
            Sentry.getCurrentScope().setTransactionName(error);
        }

        const context = {
            tags: {
                userAgent: window.navigator.userAgent
            }
        };

        return context;
    }

    private logError(error: Error): void {
        if (!error.stack) {
            return;
        }

        if (!error.message || !error.message.length) {
            const exception: Exception = {
                message: 'No message associated with exception',
                stack: error.stack
            };
            this.loggingApiService.logWarning(exception);
        } else {
            const exception: Exception = {
                message: error.message,
                stack: error.stack
            };
            this.loggingApiService.logError(exception);
        }
    }
}
